import Big from 'big.js'
import { IPool, IPoolProps, TransactionEvent } from '../types/reducerTypes'
import { periods } from './dateManipulation'
import { theseus } from '../api/theseus'
import { ethers } from 'ethers'
import { amountFromWei } from './amount'
//
export const getDeployedPools = async () => {
  return []
}

export const getPoolBySlug = async (slug: string) => {
  return await theseus.get(`/pools/guest/${slug}`)
}

const FIXED_TOTAL_SUPPLY = 1_000_000_000

export const fetchUpcomingPools: (
  type: 'labs' | 'launch',
) => Promise<IPoolProps[]> = async (
  type: 'labs' | 'launch',
): Promise<IPoolProps[]> => {
  const url = type === 'labs' ? '/pools/guest' : '/pools/guest/launch'

  return await theseus.get(url).then((res) => {
    const data = res?.data?.pools?.recommended
    if (!data) throw new Error('Could not fetch any data')
    return data.map(processPool)
  })
}

type LivePools = {
  mostTraction: IPoolProps[]
  recentlyLaunched: IPoolProps[]
  closingSoon: IPoolProps[]
  mostFunded: IPoolProps[]
  upcoming: IPoolProps[]
}

export const fetchLivePools: (type: 'labs' | 'launch') => Promise<LivePools> =
  async (type: 'labs' | 'launch'): Promise<LivePools> => {
    if (type === 'labs') {
      // At this moment we don't fetch live pools for Labs
      return {
        mostTraction: [],
        recentlyLaunched: [],
        closingSoon: [],
        mostFunded: [],
        upcoming: [],
      }
    }

    const url = '/pools/guest/launch'

    return await theseus
      .get(url)
      .then((res) => {
        const pools = res?.data?.pools
        if (!pools) throw new Error('Could not fetch any data')
        return {
          mostTraction: pools.mostTraction.map(processPool),
          recentlyLaunched: pools.recentlyLaunched.map(processPool),
          closingSoon: pools.closingSoon.map(processPool),
          mostFunded: pools.mostFunded.map(processPool),
          upcoming: pools.upcoming.map(processPool),
        }
      })
      .catch(() => {
        console.error('Could not fetch any data')
        return {
          mostTraction: [],
          recentlyLaunched: [],
          closingSoon: [],
          mostFunded: [],
          upcoming: [],
        }
      })
  }

export const fetchAllPools = async (
  search: string,
  limit: number,
  offset: number,
  status: string,
  sort: string,
): Promise<{ pools: IPoolProps[]; total: number }> => {
  return await theseus
    .get('/pools/guest/launch/all', {
      params: {
        search,
        limit,
        offset,
        status,
        sort,
      },
    })
    .then((res) => {
      const data = res?.data?.data
      if (!data) throw new Error('Could not fetch any data')
      const { pools, total } = data
      return {
        pools: pools.map(processPool),
        total,
      }
    })
}

const emptySold = {
  private: new Big('0'),
  public: new Big('0'),
  total: new Big('0'),
}

const emptyTime = {
  privatePoolSaleEndTime: 0,
  privatePoolSaleStartTime: 0,
  publicPoolSaleEndTime: 0,
  publicPoolSaleStartTime: 0,
}

function processPool(pool: IPool) {
  if (!pool) return {}
  if (!pool?.poolAddress) pool.poolAddress = '#not-live'
  const { slug } = pool
  if (!slug) return {}

  const escrowPercentageCounted = new Big(
    new Big(pool?.totalSaleSupply || 0).mul(
      new Big(pool?.escrowBlock?.escrowPercentage || 0),
    ),
  ).div(100)

  const airDrop = new Big(pool?.totalSaleSupply || 0)
    .minus(escrowPercentageCounted)
    .mul(1)
    .div(100)

  return {
    id: pool.id,
    address: pool?.poolAddress?.toLowerCase() ?? '#not-live',
    urlLogo: pool?.about?.logo?.url,
    params: {
      decimals: pool?.decimals,
      initialAddress: pool?.initialAddress,
      token: pool?.token,
      totalSaleSupply: new Big(pool?.totalSaleSupply || 0),
      poolSupplyCounted: new Big(pool?.totalSaleSupply || 0)
        .minus(escrowPercentageCounted)
        .minus(airDrop),
      privateTokenPrice: new Big(pool?.privateSale?.price ?? 0),
      publicTokenPrice: new Big(pool?.tgePrice ?? 0),
      escrowPercentage: new Big(pool?.escrowBlock?.escrowPercentage || 0),
      airDrop: new Big(pool?.airDrop || 0),
      tokenName: pool?.name,
      tokenSymbol: pool?.ticker,
      isDeployed: pool?.isDeployed,
      isOldPool: pool?.isOldPool,
      mainNet: pool?.mainNet,
      vestingPoints: '',
    },
    sold: emptySold,
    time: emptyTime,
    period: periods.upcoming,
    highlightsText: pool?.about?.highlightsText,
    media: pool?.media,
    cmsData: pool,
    slug,
  }
}

export function parseChainId(poolChainId?: string) {
  return parseInt((poolChainId ?? '_168587773').split('_').pop(), 10)
}

export function formatLegacyPools(pools: any) {
  return pools.map((pool: any) => {
    pool.privateTokenPrice = new Big(pool.privateTokenPrice)
    pool.totalSaleSupply = new Big(pool.totalSaleSupply)
    const inner = { ...pool }
    inner.period = 'completed'
    return {
      params: { ...inner },
      address: inner.cmsDetails.poolAddress,
      period: 'completed',
      urlLogo: inner?.cmsDetails?.about?.logo?.url ?? '',
      isLegacyPool: true,
    }
  })
}

export function getLatestMemecoinPriceInEth(
  latestTransaction: TransactionEvent | undefined,
  decimals: number,
) {
  if (!latestTransaction) return

  let tokenPriceInEth, ethAmount, tokenAmount

  const event = latestTransaction.event.toLowerCase()

  switch (event) {
    case 'purchase':
      ethAmount = ethers.formatEther(latestTransaction.meta.amountIn)
      tokenAmount = amountFromWei(latestTransaction.meta.amountOut, decimals)
      tokenPriceInEth = Number(ethAmount) / Number(tokenAmount)
      break
    case 'sell':
      tokenAmount = amountFromWei(latestTransaction.meta.amountIn, decimals)
      ethAmount = ethers.formatEther(latestTransaction.meta.amountOut)
      tokenPriceInEth = Number(ethAmount) / Number(tokenAmount)
      break
    default:
      throw new Error('Unknown transaction type')
  }

  return tokenPriceInEth
}

export function getMemecoinMarketCap(
  tokenPriceInEth: number,
  ethPriceInUsd: number,
  totalSupply = FIXED_TOTAL_SUPPLY,
) {
  const tokenPriceInUsd = tokenPriceInEth * ethPriceInUsd
  return totalSupply * tokenPriceInUsd
}
